import { apiClient } from '@core/api/ApiClientFactory';
import type { Shop, Organisation, CreateShopModel } from '@core/types';

export const shopService = {
    async loadShops(): Promise<Shop[]> {
        try {
            const response = await apiClient.get<Shop[]>('shop');
            return response.data;
        } catch (error) {
            console.error('[ShopService] Error loading shops:', error);
            throw error;
        }
    },

    async saveShop(shop: Shop): Promise<Shop> {
        try {
            if (shop?.id) {
                const response = await apiClient.put(`shop/${shop.id}`, shop);
                return response.data;
            }
            const response = await apiClient.post('shop', shop);
            return response.data;
        } catch (error) {
            console.error('[ShopService] Error saving shop:', error);
            throw error;
        }
    },

    async createShop(shop: CreateShopModel): Promise<string> {
        try {
            const response = await apiClient.post('shop', shop);
            return response.data;
        } catch (error) {
            console.error('[ShopService] Error saving shop:', error);
            throw error;
        }
    },

    async updateShopCloudConfig(): Promise<void> {
        try {
            await apiClient.put('shop/updateCloudConfig');
            return;
        } catch (error) {
            console.error('[ShopService] Error updating cloud config:', error);
            throw error;
        }
    },

    async loadShop(shopId: string): Promise<Shop> {
        try {
            const response = await apiClient.get<Shop>(`shop/${shopId}`);
            return response.data;
        } catch (error) {
            console.error('[ShopService] Error loading shop components:', error);
            throw error;
        }
    },

    async loadShopRecycledModules(shopId: string): Promise<Shop> {
        try {
            const response = await apiClient.get<Shop>(`shop/${shopId}/modules/recycled`);
            return response.data;
        } catch (error) {
            console.error('[ShopService] Error loading shop recycled modules:', error);
            throw error;
        }
    },

    async saveOrganisation(org: Organisation): Promise<Organisation> {
        try {
            const response = await apiClient.put('organisation', org);
            return response.data;
        } catch (error) {
            console.error('[ShopService] Error saving organisation:', error);
            throw error;
        }
    },

    async sendShopToRecycleBin(shopId: string): Promise<void> {
        try {
            await apiClient.post(`shop/${shopId}/send-to-recycle-bin`);
            return;
        } catch (error) {
            console.error('[ShopService] Error sending shop to recycle bin:', error);
            throw error;
        }
    },

    async restoreShopFromRecycleBin(shopId: string): Promise<void> {
        try {
            await apiClient.post(`shop/${shopId}/restore-from-recycle-bin`);
            return;
        } catch (error) {
            console.error('[ShopService] Error restoring shop from recycle bin:', error);
            throw error;
        }
    },

    async deleteShopPermanently(shopId: string): Promise<void> {
        try {
            await apiClient.delete(`shop/${shopId}`);
            return;
        } catch (error) {
            console.error('[ShopService] Error deleting shop permanently:', error);
            throw error;
        }
    },

    async loadRecycledShops(): Promise<Shop[]> {
        try {
            const response = await apiClient.get<Shop[]>('shop/recycledShopsIds');
            return response.data;
        } catch (error) {
            console.error('[ShopService] Error loading recycled shops:', error);
            throw error;
        }
    },

    async loadAllShopsWithRecycled(): Promise<Shop[]> {
        try {
            const response = await apiClient.get<Shop[]>('shop/getAllWithRecycled');
            return response.data;
        } catch (error) {
            console.error('[ShopService] Error loading all shops with recycled:', error);
            throw error;
        }
    }
};
